import React, { useEffect, useState } from "react";
import { Loader2, LogOut, Users, ShoppingBag, Flower2, Search, Eye, MousePointerClick, ArrowRight, Inbox } from "lucide-react";
import { toast } from "sonner";
import { analyticsService, formatApiError } from "../../services/catalogService";

// Task 24 — consolidated dashboard: key figures + recent activity in one view.
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—");

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-black/10 p-5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.16em] text-[#817B74] flex items-center gap-2">
        <Icon size={13} className="text-[#D92A47]" /> {label}
      </span>
      <div className="font-serif text-4xl text-[#252525] mt-2">{value}</div>
      {sub && <div className="text-xs text-[#817B74] mt-1">{sub}</div>}
    </div>
  );
}

function MiniBars({ title, rows, labelKey, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white border border-black/10 p-6" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">{title}</span>
      {rows.length === 0 ? (
        <p className="py-6 text-sm text-[#817B74]">No data yet.</p>
      ) : (
        <div className="space-y-2 mt-4">
          {rows.map((r) => (
            <div key={r[labelKey]} className="flex items-center gap-3" data-testid={`${testid}-${r[labelKey].replace(/\W+/g, "-")}`}>
              <span className="w-32 shrink-0 truncate text-sm text-[#252525]" title={r[labelKey]}>{r[labelKey]}</span>
              <div className="flex-1 h-4 bg-[#F6F2EC] relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0"
                  style={{ width: `${Math.max(4, (r.count / max) * 100)}%`, backgroundColor: `rgba(217,42,71,${0.25 + 0.75 * (r.count / max)})` }}
                />
              </div>
              <span className="w-10 shrink-0 text-xs text-[#817B74] text-right">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardPanel({ admin, onLogout, onNavigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    analyticsService.dashboard()
      .then(setData)
      .catch((e) => toast.error("Could not load dashboard", { description: formatApiError(e) }));
  }, []);

  if (data === null) {
    return (
      <div className="text-[#817B74] py-20 text-center" data-testid="admin-dashboard-panel">
        <Loader2 className="animate-spin inline mr-2" size={16} />Loading dashboard…
      </div>
    );
  }

  const t = data.totals;
  const w = data.last_7_days;

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-dashboard-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Dashboard</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Everything at a glance — leads, orders, catalog and visitor activity. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* Key figures */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Leads" value={t.leads} sub={`${t.leads_new} awaiting review`} testid="dash-leads" />
        <StatCard icon={ShoppingBag} label="Order requests" value={t.orders} testid="dash-orders" />
        <StatCard icon={Flower2} label="Varieties" value={t.varieties} testid="dash-varieties" />
        <StatCard icon={Search} label="Catalog searches" value={t.searches} testid="dash-searches" />
      </div>

      {/* Last 7 days */}
      <div className="bg-white border border-black/10 p-6 mb-8" data-testid="dash-week">
        <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Last 7 days</span>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4">
          {[
            [Users, "New leads", w.leads, "dash-week-leads"],
            [Inbox, "Order requests", w.orders, "dash-week-orders"],
            [Eye, "Page views", w.pageviews, "dash-week-views"],
            [MousePointerClick, "Clicks", w.clicks, "dash-week-clicks"],
          ].map(([Icon, label, value, testid]) => (
            <div key={testid} data-testid={testid}>
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#817B74] flex items-center gap-1.5">
                <Icon size={12} className="text-[#D92A47]" /> {label}
              </span>
              <div className="font-serif text-3xl text-[#252525] mt-1">{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <MiniBars title="Leads by source" rows={data.leads_by_source} labelKey="source" testid="dash-sources" />
        <MiniBars title="Orders by status" rows={data.orders_by_status} labelKey="status" testid="dash-statuses" />
      </div>

      {/* Top pages + searches */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <MiniBars
          title="Most visited pages"
          rows={data.top_pages.map((p) => ({ ...p, count: p.views + p.clicks }))}
          labelKey="path"
          testid="dash-pages"
        />
        <MiniBars title="Top searches" rows={data.top_searches} labelKey="term" testid="dash-top-searches" />
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-black/10 p-6" data-testid="dash-recent-leads">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Latest leads</span>
            <button onClick={() => onNavigate?.("leads")} data-testid="dash-goto-leads"
              className="text-[11px] uppercase tracking-[0.14em] text-[#D92A47] inline-flex items-center gap-1 hover:underline">
              View all <ArrowRight size={12} />
            </button>
          </div>
          {data.recent_leads.length === 0 ? (
            <p className="py-6 text-sm text-[#817B74]">No leads yet.</p>
          ) : (
            <ul className="divide-y divide-black/5 mt-3">
              {data.recent_leads.map((l) => (
                <li key={l.id} className="py-2.5 flex items-baseline justify-between gap-3 text-sm" data-testid={`dash-lead-${l.id}`}>
                  <span className="truncate text-[#252525]">{l.contact_name || "—"}</span>
                  <span className="shrink-0 text-xs text-[#817B74]">{l.source} · {fmtDate(l.received_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-black/10 p-6" data-testid="dash-recent-orders">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">Latest order requests</span>
            <button onClick={() => onNavigate?.("orders")} data-testid="dash-goto-orders"
              className="text-[11px] uppercase tracking-[0.14em] text-[#D92A47] inline-flex items-center gap-1 hover:underline">
              View all <ArrowRight size={12} />
            </button>
          </div>
          {data.recent_orders.length === 0 ? (
            <p className="py-6 text-sm text-[#817B74]">No order requests yet.</p>
          ) : (
            <ul className="divide-y divide-black/5 mt-3">
              {data.recent_orders.map((o) => (
                <li key={o.id} className="py-2.5 flex items-baseline justify-between gap-3 text-sm" data-testid={`dash-order-${o.id}`}>
                  <span className="truncate text-[#252525]">
                    <span className="font-mono text-xs text-[#D92A47] mr-2">{o.reference}</span>{o.contact_name}
                  </span>
                  <span className="shrink-0 text-xs text-[#817B74]">{o.status} · {fmtDate(o.received_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
